/**
 * Olea inclusions grid
 *
 * The standard inclusions are a clickable grid: thumbnail plus short label,
 * and clicking one opens a panel with its description and specs.
 *
 * Content comes from `inclusion_item` metaobject entries, so the twelve
 * components are maintained once and shared by all three pods rather than
 * copied three times.
 *
 * Panels are pre-rendered into <template> elements by Liquid and cloned into a
 * single native <dialog> on open. Using <dialog> rather than a hand-rolled
 * panel means focus trapping, Escape-to-close and the backdrop are handled by
 * the browser instead of by us.
 */

class OleaInclusions extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-olea-inc-dialog]');
    this.body = this.querySelector('[data-olea-inc-body]');
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const closer = event.target.closest('[data-olea-inc-close]');
    if (closer && this.dialog) {
      this.dialog.close();
      return;
    }

    const card = event.target.closest('[data-olea-inc-open]');
    if (!card || !this.dialog || !this.body) return;

    const key = card.getAttribute('data-olea-inc-open');
    const tpl = this.querySelector('[data-olea-inc-panel="' + CSS.escape(key) + '"]');
    if (!tpl) return;

    this.body.replaceChildren(tpl.content.cloneNode(true));

    // The card that opened the dialog gets focus back on close, so keyboard
    // users are returned to where they were rather than the top of the page.
    this.opener = card;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    this.dialog.addEventListener('close', () => {
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }, { once: true });
  }
}

if (!customElements.get('olea-inclusions')) {
  customElements.define('olea-inclusions', OleaInclusions);
}
